import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, formatPrice } from "../i18n";
import { ArrowRight, Plane, Infinity as InfinityIcon, Tag } from "lucide-react";
import { fetchCars } from "../api";
import CarCard from "../components/CarCard";
import Marquee from "../components/Marquee";
import QuickQuote from "../components/QuickQuote";
import TrustBadges from "../components/TrustBadges";
import GoogleReviews from "../components/GoogleReviews";
import HeroMedia from "../components/HeroMedia";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3c0dfd58-edf3-43de-94bb-9a0fc30d9282/images/554dab40d961d80481d6ccd1e943c6c86b47ceb594c520321cf452bb00d69c28.png";
const INTERIOR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3c0dfd58-edf3-43de-94bb-9a0fc30d9282/images/4cf9b20fe65219f92a22c72831899dee712d185fa86498ad6b5fe6160286efa5.png";

const Home = () => {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);

  useEffect(() => {
    fetchCars().then(setCars).catch(() => setCars([]));
  }, []);

  const featured = cars.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] overflow-hidden grain">
        <HeroMedia posterUrl={HERO_IMG} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-32 min-h-[90vh] flex flex-col justify-end">
          <p className="text-label text-[#FFB400]" data-testid="hero-eyebrow">
            <span className="line-accent" />
            {t.hero.eyebrow}
          </p>
          <h1
            className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tighter text-white max-w-5xl"
            data-testid="hero-title"
          >
            {t.hero.title1}
            <br />
            <span className="text-[#FFB400]">{t.hero.title2}</span>
          </h1>
          <p
            className="mt-8 max-w-2xl text-zinc-300 text-lg leading-relaxed"
            data-testid="hero-sub"
          >
            {t.hero.sub}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/fleet"
              className="btn-primary glow-coral"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary} <ArrowRight size={18} />
            </Link>
            <Link
              to="/fleet"
              className="btn-outline"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>

          <QuickQuote />
        </div>
      </section>

      <Marquee />

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-24" data-testid="trust-strip-section">
        <TrustBadges />
      </section>

      {/* Value props - core promises */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-3 gap-8">
        {[
          {
            icon: <Tag size={28} className="text-[#0a0a0a]" />,
            tag: "From $28.99/day",
            tagPt: "A partir de $28,99/dia",
            title: "Honest, upfront prices.",
            titlePt: "Preço honesto, sem letra miúda.",
            desc: "Nissan Versa from $28.99/day. Compacts from $34.99. SUVs from $59.99. No hidden surcharges at handover.",
            descPt:
              "Nissan Versa a $28,99/dia. Compactos a partir de $34,99. SUVs a partir de $59,99. Zero taxa escondida na entrega.",
          },
          {
            icon: <Plane size={28} className="text-[#0a0a0a]" />,
            tag: "Airport delivery",
            tagPt: "Entrega no aeroporto",
            title: "We meet you at Logan.",
            titlePt: "Buscamos você no Logan.",
            desc: "Land at Logan International (BOS) and we’ll hand you the keys at the curb — usually within 15 minutes of arrival.",
            descPt:
              "Pousou no Logan (BOS)? A gente te encontra na saída e entrega a chave em até 15 minutos, normalmente.",
          },
          {
            icon: <InfinityIcon size={28} className="text-[#0a0a0a]" />,
            tag: "Unlimited miles",
            tagPt: "Milhas livres",
            title: "Drive without watching the odometer.",
            titlePt: "Rode sem ficar olhando o hodômetro.",
            desc: "Cape Cod, New York, Niagara Falls — go as far as you want. Insurance and 24/7 roadside included.",
            descPt:
              "Cape Cod, Nova York, Cataratas do Niágara — vá até onde quiser. Seguro e assistência 24/7 inclusos.",
          },
        ].map((card, i) => {
          const isPt = t.nav.fleet === "Frota";
          return (
            <div
              key={i}
              className="bg-[#0f0f0f] border border-white/10 p-8 rounded-sm lift relative overflow-hidden"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-14 h-14 bg-[#FFB400] flex items-center justify-center rounded-sm mb-6">
                {card.icon}
              </div>
              <p className="text-label text-[#FFB400] mb-2">
                {isPt ? card.tagPt : card.tag}
              </p>
              <h3 className="font-heading text-2xl font-medium text-white">
                {isPt ? card.titlePt : card.title}
              </h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">
                {isPt ? card.descPt : card.desc}
              </p>
            </div>
          );
        })}
      </section>

      {/* Featured fleet */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="featured-fleet">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-label text-[#FFB400]">
              <span className="line-accent" />
              {t.fleet.eyebrow}
            </p>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white max-w-2xl">
              {t.fleet.title}
            </h2>
          </div>
          <Link
            to="/fleet"
            className="btn-outline"
            data-testid="see-all-fleet-link"
          >
            {t.common.viewDetails} <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((c) => (
            <CarCard key={c.id} car={c} />
          ))}
        </div>
      </section>

      {/* Interior CTA */}
      <section className="relative grain my-24" data-testid="interior-section">
        <div className="absolute inset-0">
          <img
            src={INTERIOR_IMG}
            alt="Car keys handover"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <p className="text-label text-[#FFB400]">Movix Service</p>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white max-w-2xl">
            Pickup at Logan or our Everett lot.
          </h2>
          <p className="mt-6 text-zinc-300 max-w-xl leading-relaxed">
            Land at Logan and we’ll meet you at the curb. Prefer to come over?
            Our lot at 70 Chelsea St is a 6-minute drive from the airport.
          </p>
          <Link
            to="/contact"
            className="mt-10 inline-flex btn-primary"
            data-testid="concierge-cta"
          >
            {t.common.learnMore} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Price strip */}
      <section className="border-y border-white/10 bg-[#080808]" data-testid="price-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-label">{t.common.from}</p>
            <p className="font-heading text-4xl font-bold text-white">
              {formatPrice(28.99)}
              <span className="text-sm text-zinc-500 font-mono-ui ml-2">
                {t.common.perDay}
              </span>
            </p>
          </div>
          <p className="text-zinc-400 max-w-md">
            Logan Airport pickup, unlimited miles, insurance and 24/7 roadside —
            all included. No surprises at the counter.
          </p>
          <Link to="/fleet" className="btn-primary" data-testid="price-strip-cta">
            {t.nav.book} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <GoogleReviews />
    </div>
  );
};

export default Home;
